/* Searchable model pickers (split from CDMDocumentsSettings.jsx) */
import React from 'react';
import { Button } from '../../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../../components/ui/command';
import { ChevronsUpDown, RefreshCw, X } from 'lucide-react';
import { MODALITY_BADGES, FILTER_TABS } from './constants';

/* Searchable model picker for Language Detection (and other single-model selectors).
   Uses the same OpenRouter model list as ModelCombobox but simplified for single selection.
   Falls back to showing the saved value if model list fails to load. */
export function LangDetectModelPicker({ value, models, loading, onChange, onRetry }) {
  const [open, setOpen] = React.useState(false);
  const selectedModel = models.find(m => m.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-8 text-xs font-normal"
          data-testid="lang-detect-model-picker"
        >
          <span className="truncate">
            {loading ? 'Loading models...' : selectedModel ? (selectedModel.name || selectedModel.id) : value || 'Select model...'}
          </span>
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[480px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search models (e.g. haiku, sonnet, gpt)..." className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty>
              {models.length === 0 ? (
                <div className="py-3 text-center space-y-1">
                  <p className="text-xs text-gray-500">Couldn't load OpenRouter models</p>
                  {value && <p className="text-[10px] text-gray-400">Using saved: {value}</p>}
                  <Button variant="ghost" size="sm" className="text-xs h-6" onClick={(e) => { e.stopPropagation(); onRetry?.(); }}>
                    <RefreshCw className="h-3 w-3 mr-1" /> Retry
                  </Button>
                </div>
              ) : 'No model found.'}
            </CommandEmpty>
            <CommandGroup>
              {models.length === 0 && value && (
                <CommandItem
                  value={value}
                  onSelect={() => { onChange(value); setOpen(false); }}
                  className="text-xs"
                >
                  <span className="font-semibold">{value}</span>
                  <span className="ml-auto text-[10px] text-gray-400">saved</span>
                </CommandItem>
              )}
              {models.map((m) => (
                <CommandItem
                  key={m.id}
                  value={`${m.name} ${m.id}`}
                  onSelect={() => { onChange(m.id); setOpen(false); }}
                  className="text-xs"
                >
                  <span className={`truncate ${value === m.id ? 'font-semibold' : ''}`}>{m.name || m.id}</span>
                  <span className="ml-auto text-[10px] text-gray-400 shrink-0">{m.id}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/* Searchable model combobox with capability filter tabs */
export function ModelCombobox({ idx, label, value, models, onChange, defaultFilter }) {
  const [open, setOpen] = React.useState(false);
  const [activeFilter, setActiveFilter] = React.useState(defaultFilter || 'all');
  const selectedModel = models.find(m => m.id === value);

  const filteredModels = React.useMemo(() => {
    if (activeFilter === 'all') return models;
    if (activeFilter === 'web') return models.filter(m => m.has_web_search);
    return models.filter(m => (m.input_modalities || []).includes(activeFilter));
  }, [models, activeFilter]);

  const getModelBadges = (model) => {
    const badges = [];
    const mods = model.input_modalities || [];
    if (mods.includes('file')) badges.push('file');
    if (mods.includes('image')) badges.push('image');
    if (mods.includes('video')) badges.push('video');
    if (mods.includes('audio')) badges.push('audio');
    if (model.has_web_search) badges.push('web');
    return badges;
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500 w-24 shrink-0">{label}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="flex-1 justify-between h-8 text-xs font-normal"
            data-testid={`detection-model-${idx}`}
          >
            <span className="truncate flex items-center gap-1.5">
              {selectedModel ? (
                <>
                  <span>{selectedModel.name || selectedModel.id}</span>
                  {getModelBadges(selectedModel).map(b => (
                    <span key={b} className={`inline-block px-1 py-0 rounded text-[9px] font-medium ${MODALITY_BADGES[b]?.className || ''}`}>
                      {MODALITY_BADGES[b]?.label}
                    </span>
                  ))}
                </>
              ) : (
                <span className="text-gray-400">{idx === 0 ? 'Select model...' : 'None (optional)'}</span>
              )}
            </span>
            <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[480px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search models..." className="h-8 text-xs" />
            {/* Filter tabs */}
            <div className="flex gap-1 px-2 py-1.5 border-b bg-gray-50">
              {FILTER_TABS.map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveFilter(tab.key)}
                  className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                    activeFilter === tab.key
                      ? 'bg-gray-800 text-white'
                      : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'
                  }`}
                  data-testid={`filter-tab-${tab.key}`}
                >
                  {tab.label}
                </button>
              ))}
              <span className="ml-auto text-[10px] text-gray-400 self-center">{filteredModels.length} models</span>
            </div>
            <CommandList>
              <CommandEmpty>No model found for this filter.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="_clear_"
                  onSelect={() => { onChange(null); setOpen(false); }}
                  className="text-xs text-gray-400"
                >
                  {idx === 0 ? 'Select model...' : 'None (optional)'}
                </CommandItem>
                {filteredModels.map((m) => {
                  const badges = getModelBadges(m);
                  return (
                    <CommandItem
                      key={m.id}
                      value={`${m.name} ${m.id}`}
                      onSelect={() => { onChange(m.id); setOpen(false); }}
                      className="text-xs flex items-center justify-between"
                    >
                      <span className={`truncate ${value === m.id ? 'font-semibold' : ''}`}>{m.name || m.id}</span>
                      <span className="flex gap-0.5 shrink-0 ml-2">
                        {badges.map(b => (
                          <span key={b} className={`inline-block px-1 py-0 rounded text-[9px] font-medium ${MODALITY_BADGES[b]?.className || ''}`}>
                            {MODALITY_BADGES[b]?.label}
                          </span>
                        ))}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-gray-400 hover:text-gray-600 shrink-0"
          title="Clear selection"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
